import { CSSObject, SxProps, Theme } from '@mui/material';

// Clearance for the floating personalize tool that the column pins above the cards.
export const FLOATING_TOOLS_CLEARANCE = '2.75rem';

// One gutter for every resizable right panel (conversation, site peek, site full page).
export const RIGHT_PANEL_RESIZE_GUTTER_WIDTH = '0.25rem';

// Side-panel scrollers keep the browser's default bar: nothing else in the app styles
// its scrollbars, so a narrower custom one here would read as a different surface.
export const getSidePanelScrollbarSx = (): CSSObject => ({
  scrollbarWidth: 'auto',
});

// Gap between a scrolling column and the panel's rounded frame, so the frame's border
// does not clip the scrollbar painted at the column's edge.
export const SCROLLBAR_FRAME_CLEARANCE = '0.375rem';

export const getEntityCardIconSx = (color: string): SxProps<Theme> => ({
  flexShrink: 0,
  width: '1.25rem',
  height: '1.25rem',
  fontSize: '1.25rem',
  color,
});

export const getSidePanelCardsSx = ({
  hasFloatingTools,
  extendedView,
  isMobile,
}: {
  hasFloatingTools: boolean;
  extendedView?: boolean;
  isMobile?: boolean;
}): SxProps<Theme> => ({
  // prototype insets: peek stacks 10px scroller + 8px embed body; the full-page rail has only the 8px body
  // On desktop the host already spends part of the right inset on scrollbar clearance
  // (the embed body's 0.25rem, plus the peek scroller's SCROLLBAR_FRAME_CLEARANCE), so
  // the cards give up that amount on their right — otherwise the visible right inset
  // outgrows the left one.
  padding: (() => {
    if (extendedView) {
      return isMobile ? '0.5rem 0.5rem 0.5rem' : '0.5rem 0.25rem 0.5rem 0.5rem';
    }
    return isMobile ? '0.5rem 1.125rem 1.25rem' : '0.5rem 0.5rem 1.25rem 1.125rem';
  })(),
  // clear the floating tool so the first card starts below it, then scrolls under
  ...(hasFloatingTools && { paddingTop: FLOATING_TOOLS_CLEARANCE }),
});

// The personalize tool floats over the cards just under the tabs band. In the full-page
// layout the rail sits below the full-width tab strip (SidePanelTabs spans content + rail),
// so the sticky offset only needs to clear the rail's own top inset, not the tabs height too.
export const FULL_PAGE_FLOATING_TOOLS_TOP = '0.5rem';

export const getPanelToolsSlotSx = (isMobile: boolean): SxProps<Theme> => ({
  position: 'sticky',
  top: isMobile ? '0.5rem' : FULL_PAGE_FLOATING_TOOLS_TOP,
  zIndex: 3,
  height: 0,
  display: 'flex',
  // the slot has no height on purpose; centering would lift the tool into the header
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  gap: '0.5rem',
  paddingRight: '0.5rem',
  pointerEvents: 'none',
  '& > *': { pointerEvents: 'auto' },
});

export const getEntityDetailsRailSx = ({
  isMobile,
  sidePanelWidth,
  isDragging,
  isOpen,
}: {
  isMobile: boolean;
  sidePanelWidth: number;
  isDragging: boolean;
  isOpen: boolean;
}): SxProps<Theme> => ({
  width: isMobile ? '100%' : sidePanelWidth,
  // The stored width can exceed what the row has left (narrow container, wide drag);
  // without this cap the rail's right edge — and its scrollbar — get pushed past the
  // clipping box instead of the rail giving up width on its left side.
  ...(!isMobile && { maxWidth: `calc(100% - ${RIGHT_PANEL_RESIZE_GUTTER_WIDTH})` }),
  ...(isDragging && { transition: 'none' }),
  zIndex: 1,
  // This column is the scroller: the cards inside grow to content height.
  overflow: isOpen ? 'hidden auto' : 'hidden',
  ...getSidePanelScrollbarSx(),
});

// Closes the grid's right edge against the rail — without it the table rows end in
// mid-air, since the content/rail split carries no separator of its own.
export const FULL_PAGE_CONTENT_COLUMN_SX: SxProps<Theme> = (theme) => ({
  position: 'relative',
  width: 0,
  overflow: 'auto',
  borderRight: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
});

// Full-page branch: SidePanelTabs spans content + rail, so its BlockContent must lay out
// that row itself instead of scrolling as a single column — each side scrolls on its own.
export const SIDE_PANEL_TABS_FULL_PAGE_INNER_STYLES = {
  padding: 0,
  display: 'flex',
  flexDirection: 'row',
  minHeight: 0,
  overflow: 'hidden',
};
